/**
 * Notebook route DOM activation — Next.js 16 Activity keeps prior .nb-root trees.
 * Only one root may be active; stale roots must be hidden before paint.
 */

import { resetNotebookEditorSurface } from './tiptap-lifecycle'
import { dedupeNotebookBlockSlots } from './dedupe-notebook-block-slots'
import { nbWarn } from './notebook-debug'
import {
  deactivateNotebookRoot,
  evictStaleNotebookRouteMounts,
} from './notebook-route-root'

const ROOT_SELECTOR = '.nb-root[data-nb-route-root]'

/**
 * @param {Element} keepRoot
 * @param {string | null | undefined} notebookId
 */
export function claimNotebookRouteRoot(keepRoot, notebookId = null) {
  if (typeof document === 'undefined' || !keepRoot) return

  const roots = [...document.querySelectorAll(ROOT_SELECTOR)]

  for (const root of roots) {
    if (root !== keepRoot) {
      deactivateNotebookRoot(root)
    }
  }

  let removed = 0
  for (const root of roots) {
    if (root !== keepRoot) {
      root.remove()
      removed += 1
    }
  }

  keepRoot.dataset.nbRouteActive = 'true'
  if (notebookId) {
    keepRoot.setAttribute('data-nb-notebook-id', notebookId)
  }
  keepRoot.removeAttribute('hidden')
  keepRoot.removeAttribute('inert')
  keepRoot.removeAttribute('aria-hidden')
  keepRoot.style.removeProperty('display')

  if (removed > 0) {
    resetNotebookEditorSurface()
    nbWarn('Claimed notebook route root — removed stale Activity trees', {
      removed,
      notebookId,
      kept: keepRoot.getAttribute('data-nb-route-root-id'),
    })
  }

  evictStaleNotebookRouteMounts(
    keepRoot.querySelector('.nb-route-mount[data-nb-route-active-mount="true"]') ??
      keepRoot.querySelector('.nb-route-mount'),
  )
  dedupeNotebookBlockSlots(keepRoot)
}

/** @param {Element | null | undefined} root */
export function releaseNotebookRouteRoot(root) {
  if (!root) return
  deactivateNotebookRoot(root)
}

/**
 * Post-hydration hygiene inside the active root (duplicate slots / route mounts).
 * @param {ParentNode | null | undefined} scope
 */
export function syncNotebookRouteDom(scope = null) {
  if (typeof document === 'undefined') return
  const root =
    scope instanceof Element
      ? scope.closest(ROOT_SELECTOR) ?? scope
      : document.querySelector(`${ROOT_SELECTOR}[data-nb-route-active="true"]`)

  if (!root) return

  evictStaleNotebookRouteMounts(
    root.querySelector('.nb-route-mount[data-nb-route-active-mount="true"]') ??
      root.querySelector('.nb-route-mount'),
  )
  dedupeNotebookBlockSlots(root)
}
